import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { KundeKortService } from 'src/app/services/kunde-kort.service';
import { MembershipV2Service } from 'src/app/services/membership-v2.service';
import { AuthService } from 'src/app/services/auth.service';

// Most company-paid machines charge this per free-vend cup (Oct 2026).
export const COFFEE_DEFAULT_PRICE_EX_VAT = 10.5;

// Purchase types a machine can bill. TEST (service vends) is never billed.
export const COFFEE_PAYMENT_TYPES: { key: string, label: string, hint: string }[] = [
  { key: 'FREEVEND', label: 'Free', hint: 'Free to the employee — the company pays per cup' },
  { key: 'CASHLESS', label: 'Card', hint: 'The employee paid by card — billed to the company too only where agreed' }
];

interface MachineRow {
  vendonMachineId: number;
  name: string;
  counts: { [type: string]: number } | null;   // this month's vends per purchase type (null = Vendon failed for it)
  configured: boolean;                          // has a vendon-machines doc
  // editable — mirrors vendon-machines/{vendonMachineId}
  billable: boolean;
  kundekortId: string | null;
  systemId: string | null;
  pricePerCupExVat: number | null;
  paymentTypes: string[];
  reason: string;
  dirty: boolean;
  saving: boolean;
  error: string;
}

// Coffee machines: which customer each Vendon machine bills, at what price and
// for which cups. Cups themselves come from Vendon; the setup is stored in
// `vendon-machines/{vendonMachineId}`, which /vendonCoffeeBillable reads when
// invoices are built — so a change here applies to the next invoice run, for
// whichever month is being invoiced.
@Component({
  selector: 'coffee-machines',
  templateUrl: './coffee-machines.component.html',
  styleUrls: ['../../shared/styles/crm-billing.css', './coffee-machines.component.css']
})
export class CoffeeMachinesComponent implements OnInit, OnChanges, OnDestroy {

  // Driven by the Billing page's month picker, like <garantiomsetning>.
  @Input() month: string = moment().format('YYYY-MM');
  paymentTypes = COFFEE_PAYMENT_TYPES;
  loading = false;
  loadError = '';
  vendonErrors: string[] = [];
  rows: MachineRow[] = [];
  customers: { id: string, name: string }[] = [];
  systems: { id: string, name: string }[] = [];
  private adminEmail = '';
  private subs: Subscription[] = [];
  private initialized = false;

  constructor(
    private afs: AngularFirestore,
    private kundeKort: KundeKortService,
    private membership: MembershipV2Service,
    private auth: AuthService
  ) { }

  ngOnInit() {
    this.subs.push(this.auth.user$.subscribe(u => this.adminEmail = u ? u.email : ''));
    this.subs.push(this.kundeKort.getCustomers().subscribe((c: any[]) => {
      this.customers = (c || []).map(x => ({ id: x.kundekortId, name: x.name || '(unnamed)' }))
        .sort((a, b) => a.name.localeCompare(b.name, 'nb'));
    }));
    this.afs.collection('system_info').ref.get().then(snap => {
      this.systems = snap.docs.map(d => ({ id: d.id, name: (d.data() as any).name || d.id }))
        .sort((a, b) => a.name.localeCompare(b.name, 'nb'));
    }).catch(() => { });
    this.initialized = true;
    this.load();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['month'] && !changes['month'].firstChange && this.initialized) { this.load(); }
  }

  ngOnDestroy() { this.subs.forEach(s => s.unsubscribe()); }

  async load() {
    this.loading = true; this.loadError = ''; this.vendonErrors = [];
    try {
      const res: any = await this.membership.vendonMachines(this.month).toPromise();
      if (!res || res.success === false) { throw new Error((res && res.message) || 'no response'); }
      this.vendonErrors = res.errors || [];
      this.rows = (res.machines || []).map((m: any) => this.toRow(m));
    } catch (err: any) {
      this.loadError = `Could not read the coffee machines from Vendon: ${(err && err.message) || 'network error'}.`;
    }
    this.loading = false;
  }

  private toRow(m: any): MachineRow {
    const c = m.config || {};
    return {
      vendonMachineId: m.vendonMachineId,
      name: m.name,
      counts: m.counts,
      configured: !!m.config,
      billable: c.billable === true,
      kundekortId: c.kundekortId || null,
      systemId: c.systemId || null,
      pricePerCupExVat: c.pricePerCupExVat != null ? Number(c.pricePerCupExVat) : null,
      paymentTypes: Array.isArray(c.paymentTypes) && c.paymentTypes.length ? c.paymentTypes.slice() : ['FREEVEND'],
      reason: c.reason || '',
      dirty: false, saving: false, error: ''
    };
  }

  // ---- derived figures --------------------------------------------------------
  cups(r: MachineRow, type: string) { return (r.counts && r.counts[type]) || 0; }
  billedCups(r: MachineRow) { return r.paymentTypes.reduce((n, t) => n + this.cups(r, t), 0); }
  amount(r: MachineRow) { return r.billable ? +(this.billedCups(r) * (Number(r.pricePerCupExVat) || 0)).toFixed(2) : 0; }
  hasSales(r: MachineRow) { return !!r.counts && Object.keys(r.counts).some(t => t !== 'TEST' && r.counts[t] > 0); }
  get total() { return this.rows.reduce((n, r) => n + this.amount(r), 0); }
  get unsetCount() { return this.rows.filter(r => !r.configured).length; }
  customerName(id: string | null) { const c = this.customers.find(x => x.id === id); return c ? c.name : ''; }

  // ---- editing ----------------------------------------------------------------
  touch(r: MachineRow) { r.dirty = true; r.error = ''; }

  setBillable(r: MachineRow, on: boolean) {
    r.billable = on;
    if (on && r.pricePerCupExVat == null) { r.pricePerCupExVat = COFFEE_DEFAULT_PRICE_EX_VAT; }
    this.touch(r);
  }

  toggleType(r: MachineRow, type: string) {
    r.paymentTypes = r.paymentTypes.includes(type) ? r.paymentTypes.filter(t => t !== type) : [...r.paymentTypes, type];
    this.touch(r);
  }

  async save(r: MachineRow) {
    if (r.billable) {
      if (!r.kundekortId) { r.error = 'Choose who pays.'; return; }
      if (!(Number(r.pricePerCupExVat) > 0)) { r.error = 'Enter a price per cup.'; return; }
      if (!r.paymentTypes.length) { r.error = 'Choose which cups are billed.'; return; }
    }
    r.saving = true; r.error = '';
    try {
      await this.afs.collection('vendon-machines').doc(String(r.vendonMachineId)).set({
        vendonMachineId: r.vendonMachineId,
        vendonName: r.name,
        billable: r.billable,
        kundekortId: r.kundekortId || null,
        // The system the coffee counts toward for garantiomsetning, even when not billed.
        systemId: r.systemId || null,
        pricePerCupExVat: r.billable ? Number(r.pricePerCupExVat) : (r.pricePerCupExVat != null ? Number(r.pricePerCupExVat) : null),
        paymentTypes: r.paymentTypes,
        reason: r.billable ? '' : (r.reason || ''),
        updatedAt: Date.now(),
        updatedBy: this.adminEmail || null
      }, { merge: true });
      r.configured = true; r.dirty = false;
    } catch (err: any) {
      r.error = `Could not save (${(err && err.message) || 'permissions or network error'}).`;
    }
    r.saving = false;
  }

  trackRow(_i: number, r: MachineRow) { return r.vendonMachineId; }
}
